<div class="website-content" style="max-width:72rem;padding:0 var(--wa-spacing-x-large) var(--wa-spacing-x-large) var(--wa-spacing-x-large);">
	<div class="wa-stack wa-gap-xl">
		<div class="wa-stack wa-gap-m content-section" id="about-intro">
			<wa-divider class="section-divider"></wa-divider>
			<div class="wa-stack wa-gap-s">
				<div class="hero-eyebrow" waCaption="s" waFontWeight="semibold" waText>ABOUT JWEBMP</div>
				<h2 waHeading="l" waText>One Language to Build It All</h2>
				<p waBody="m" waColorText="quiet" waText>Java developers should not need to learn a second ecosystem to ship a web application.</p>
			</div>
			<div class="wa-stack wa-gap-m">
				<p waBody="l" waText>JWebMP lets you build complete web applications in pure Java. Add the Angular plugin and the build generates a full Angular 21 SPA from annotated classes &mdash; no hand-written TypeScript. Or run in hosted mode on Vert.x 5 with GuicedEE backend services.</p>
				<p waBody="l" waText>Either way, your component code stays the same: just Java, all the way down.</p>
				<div class="wa-cluster wa-gap-s">
					<wa-tag pill variant="brand">Java 25+</wa-tag>
					<wa-tag pill variant="danger">Angular 21</wa-tag>
					<wa-tag pill variant="warning">Vert.x 5</wa-tag>
					<wa-tag pill variant="success">JPMS</wa-tag>
					<wa-tag pill variant="neutral">Apache 2.0</wa-tag>
				</div>
				<div class="wa-grid wa-gap-m" style="--min-column-size:14rem;">
					<wa-card appearance="outlined" class="feature-card">
						<div class="wa-stack wa-gap-s">
							<h3 class="feature-card-title" waHeading="m" waText>Single Language, Full Stack</h3>
							<p waBody="m" waColorText="quiet" waText>Backend services, frontend components, data models, and routing &mdash; all in Java. No JavaScript. No HTML templates. The framework handles the rest.</p>
						</div>
					</wa-card>
					<wa-card appearance="outlined" class="feature-card">
						<div class="wa-stack wa-gap-s">
							<h3 class="feature-card-title" waHeading="m" waText>Compile-Time Safety</h3>
							<p waBody="m" waColorText="quiet" waText>Every HTML element, CSS property, and event handler is a Java type. Catch errors in your IDE, not in the browser.</p>
						</div>
					</wa-card>
					<wa-card appearance="outlined" class="feature-card">
						<div class="wa-stack wa-gap-s">
							<h3 class="feature-card-title" waHeading="m" waText>Rapid Development</h3>
							<p waBody="m" waColorText="quiet" waText>Add a dependency, annotate a class, build. Components, pages, and routes appear automatically. One command to ship a standalone SPA or a hosted app.</p>
						</div>
					</wa-card>
				</div>
				<div class="wa-cluster wa-gap-s">
					<wa-button [routerLink]="['/getting-started']" variant="brand">Get Started</wa-button>
					<wa-button [routerLink]="['/plugins']" appearance="outlined" variant="neutral">Browse Plugins</wa-button>
				</div>
			</div>
		</div>
		<div class="wa-stack wa-gap-m content-section" id="annotation-driven">
			<wa-divider class="section-divider"></wa-divider>
			<div class="wa-stack wa-gap-s">
				<div class="hero-eyebrow" waCaption="s" waFontWeight="semibold" waText>ANNOTATION-DRIVEN</div>
				<h2 waHeading="l" waText>Java Annotations Become Angular Applications</h2>
				<p waBody="m" waColorText="quiet" waText>&#64;NgComponent + &#64;NgRoutable + build = production SPA.</p>
			</div>
			<div class="wa-stack wa-gap-m">
				<markdown [data]="mdContent_9"></markdown>
				<div waCaption="s" waColorText="quiet" waFontWeight="semibold" waText>Node.js is required at build time for the Angular CLI. The resulting SPA is static and can be served from any host &mdash; the JWebMP server is entirely optional.</div>
				<wa-details summary="Java Source — SalesDashboard.java">
					<markdown [data]="mdContent_10" class="aside-snippet-code wa-body-s" clipboard lineNumbers></markdown>
				</wa-details>
				<wa-details summary="Generated Angular Project">
					<div class="file-explorer">
						<wa-tree (wa-selection-change)="onFileTreeSelect($event)" selection="single" style="--indent-size:var(--wa-spacing-small);">
							<wa-tree-item expanded>src/app/sales-dashboard/
								<wa-tree-item data-panel="tab-ts" selected>SalesDashboard.ts</wa-tree-item>
								<wa-tree-item data-panel="tab-html">SalesDashboard.html</wa-tree-item>
								<wa-tree-item data-panel="tab-grid">SampleSalesGrid.ts</wa-tree-item>
								<wa-tree-item data-panel="tab-grid-html">SampleSalesGrid.html</wa-tree-item>
								<wa-tree-item data-panel="tab-chart">SampleRevenueChart.ts</wa-tree-item>
								<wa-tree-item data-panel="tab-chart-html">SampleRevenueChart.html</wa-tree-item>
							</wa-tree-item>
							<wa-tree-item>src/app/
								<wa-tree-item data-panel="tab-routes">app.routes.ts</wa-tree-item>
							</wa-tree-item>
						</wa-tree>
						<div class="code-canvas">
							<markdown [data]="mdContent_11" [hidden]="activeFile !== 'tab-ts'" class="aside-snippet-code wa-body-s" clipboard lineNumbers></markdown>
							<markdown [data]="mdContent_12" [hidden]="activeFile !== 'tab-html'" class="aside-snippet-code wa-body-s" clipboard lineNumbers></markdown>
							<markdown [data]="mdContent_13" [hidden]="activeFile !== 'tab-grid'" class="aside-snippet-code wa-body-s" clipboard lineNumbers></markdown>
							<markdown [data]="mdContent_14" [hidden]="activeFile !== 'tab-grid-html'" class="aside-snippet-code wa-body-s" clipboard lineNumbers></markdown>
							<markdown [data]="mdContent_15" [hidden]="activeFile !== 'tab-chart'" class="aside-snippet-code wa-body-s" clipboard lineNumbers></markdown>
							<markdown [data]="mdContent_16" [hidden]="activeFile !== 'tab-chart-html'" class="aside-snippet-code wa-body-s" clipboard lineNumbers></markdown>
							<markdown [data]="mdContent_17" [hidden]="activeFile !== 'tab-routes'" class="aside-snippet-code wa-body-s" clipboard lineNumbers></markdown>
						</div>
					</div>
				</wa-details>
				<div waCaption="s" waFontWeight="semibold" waText>Expand the panels above to see the Java source and the generated Angular project. One language, full production SPA.</div>
				<wa-details summary="By the Numbers — Java vs Generated TypeScript">
					<div class="wa-grid wa-gap-m" style="--min-column-size:14rem;">
						<wa-card appearance="outlined" class="feature-card">
							<div class="wa-stack wa-gap-s">
								<h3 class="feature-card-title" waHeading="m" waText>Lines of Code</h3>
								<p waBody="m" waColorText="quiet" waText>3 Java files &mdash; ~80 LoC. The build generates 9 Angular files totalling ~330 LoC of TypeScript, HTML, and SCSS. That is a 4&times; amplification ratio you never have to write or maintain.</p>
								<div class="feature-card-note" waCaption="s" waColorText="quiet" waFontWeight="semibold" waText>80 LoC &rarr; 330 LoC &#40;4&times; amplification&#41;</div>
							</div>
						</wa-card>
						<wa-card appearance="outlined" class="feature-card">
							<div class="wa-stack wa-gap-s">
								<h3 class="feature-card-title" waHeading="m" waText>Estimated AI Tokens</h3>
								<p waBody="m" waColorText="quiet" waText>An LLM needs ~350 tokens to produce the Java source. Generating the equivalent TypeScript, templates, subscriptions, and lifecycle wiring directly would consume ~2,800 tokens &mdash; an 8&times; cost multiplier.</p>
								<div class="feature-card-note" waCaption="s" waColorText="quiet" waFontWeight="semibold" waText>~350 tokens &rarr; ~2,800 tokens &#40;8&times; cost&#41;</div>
							</div>
						</wa-card>
						<wa-card appearance="outlined" class="feature-card">
							<div class="wa-stack wa-gap-s">
								<h3 class="feature-card-title" waHeading="m" waText>Complexity</h3>
								<p waBody="m" waColorText="quiet" waText>The Java side is declarative: annotations, column definitions, and a data list. The generated TypeScript includes EventBus subscriptions, signal/computed reactivity, lifecycle hooks, resize handlers, and UUID-based listener management &mdash; all auto-wired.</p>
								<div class="feature-card-note" waCaption="s" waColorText="quiet" waFontWeight="semibold" waText>Declarative &rarr; Imperative &#40;auto-generated&#41;</div>
							</div>
						</wa-card>
					</div>
				</wa-details>
			</div>
		</div>
		<div class="wa-stack wa-gap-m content-section" id="reactive-stack">
			<wa-divider class="section-divider"></wa-divider>
			<div class="wa-stack wa-gap-s">
				<div class="hero-eyebrow" waCaption="s" waFontWeight="semibold" waText>REACTIVE STACK</div>
				<h2 waHeading="l" waText>Built on Vert.x 5 and GuicedEE</h2>
				<p waBody="m" waColorText="quiet" waText>Non-blocking I/O, dependency injection, and SPI discovery. Optional backend for Angular SPAs.</p>
			</div>
			<div class="wa-stack wa-gap-m">
				<div class="wa-grid wa-gap-m" style="--min-column-size:20rem;">
					<wa-card appearance="outlined" class="feature-card">
						<div class="wa-stack wa-gap-s">
							<h3 class="feature-card-title" waHeading="m" waText>Vert.x 5 Server</h3>
							<p waBody="m" waColorText="quiet" waText>Non-blocking event-loop server that renders pages, handles AJAX events, and serves resources. Angular SPAs can connect for push updates via event bus or RabbitMQ.</p>
							<div class="feature-card-note" waCaption="s" waColorText="quiet" waFontWeight="semibold" waText>Event-loop I/O</div>
						</div>
					</wa-card>
					<wa-card appearance="outlined" class="feature-card">
						<div class="wa-stack wa-gap-s">
							<h3 class="feature-card-title" waHeading="m" waText>GuicedEE Runtime</h3>
							<p waBody="m" waColorText="quiet" waText>Google Guice dependency injection with SPI discovery, call-scoped requests, and JPMS isolation. Included in hosted mode; optional backend for Angular SPAs.</p>
							<div class="feature-card-note" waCaption="s" waColorText="quiet" waFontWeight="semibold" waText>DI + SPI + JPMS</div>
						</div>
					</wa-card>
					<wa-card appearance="outlined" class="feature-card">
						<div class="wa-stack wa-gap-s">
							<h3 class="feature-card-title" waHeading="m" waText>AJAX Event Pipeline</h3>
							<p waBody="m" waColorText="quiet" waText>Browser events fire Java handlers on the server. 50+ event adapters &mdash; click, change, submit, drag, keyboard &mdash; all without writing any JavaScript.</p>
							<div class="feature-card-note" waCaption="s" waColorText="quiet" waFontWeight="semibold" waText>50+ event adapters</div>
						</div>
					</wa-card>
					<wa-card appearance="outlined" class="feature-card">
						<div class="wa-stack wa-gap-s">
							<h3 class="feature-card-title" waHeading="m" waText>Dual Rendering</h3>
							<p waBody="m" waColorText="quiet" waText>Same component tree, two output formats. HTML for server-side pages, JSON for AJAX responses. Angular mode adds a third: generated TypeScript.</p>
							<div class="feature-card-note" waCaption="s" waColorText="quiet" waFontWeight="semibold" waText>Hosted + SPA + AJAX</div>
						</div>
					</wa-card>
				</div>
				<div class="wa-cluster wa-gap-s">
					<wa-button [routerLink]="['/architecture']" variant="brand">Explore Architecture</wa-button>
				</div>
			</div>
		</div>
		<div class="wa-stack wa-gap-m content-section" id="real-time-overview">
			<wa-divider class="section-divider"></wa-divider>
			<div class="wa-stack wa-gap-s">
				<div class="hero-eyebrow" waCaption="s" waFontWeight="semibold" waText>CLIENT-SERVER COMMUNICATION</div>
				<h2 waHeading="l" waText>REST, AJAX, Data Services, and Real-Time</h2>
				<p waBody="m" waColorText="quiet" waText>Request/response first. Push updates when you need them.</p>
			</div>
			<div class="wa-stack wa-gap-m">
				<markdown [data]="mdContent_18"></markdown>
				<div waCaption="s" waColorText="quiet" waFontWeight="semibold" waText>Start with request/response. Layer in push updates when your application needs them.</div>
				<div class="code-block-wrapper">
					<div class="code-block-label" waCaption="s" waFontWeight="semibold" waText>REST endpoint &mdash; always available</div>
					<markdown [data]="mdContent_19" class="aside-snippet-code wa-body-s" clipboard lineNumbers></markdown>
				</div>
				<div class="code-block-wrapper">
					<div class="code-block-label" waCaption="s" waFontWeight="semibold" waText>Push updates &mdash; opt-in when ready</div>
					<markdown [data]="mdContent_20" class="aside-snippet-code wa-body-s" clipboard lineNumbers></markdown>
				</div>
				<div class="wa-cluster wa-gap-s">
					<wa-button [routerLink]="['/real-time']" variant="brand">Communication Patterns</wa-button>
				</div>
			</div>
		</div>
		<div class="wa-stack wa-gap-m content-section" id="developer-experience">
			<wa-divider class="section-divider"></wa-divider>
			<div class="wa-stack wa-gap-s">
				<div class="hero-eyebrow" waCaption="s" waFontWeight="semibold" waText>DEVELOPER EXPERIENCE</div>
				<h2 waHeading="l" waText>Built for Java Engineers</h2>
				<p waBody="m" waColorText="quiet" waText>Fluent APIs, compile-time checks, Maven integration, and full IDE support.</p>
			</div>
			<div class="wa-stack wa-gap-m">
				<div class="wa-grid wa-gap-m" style="--min-column-size:20rem;">
					<wa-card appearance="outlined" class="feature-card">
						<div class="wa-stack wa-gap-s">
							<h3 class="feature-card-title" waHeading="m" waText>Fluent API</h3>
							<p waBody="m" waColorText="quiet" waText>Chainable builders on every component. Set appearance, add children, configure options &mdash; all in one readable expression.</p>
							<div class="feature-card-note" waCaption="s" waColorText="quiet" waFontWeight="semibold" waText>CRTP generics</div>
						</div>
					</wa-card>
					<wa-card appearance="outlined" class="feature-card">
						<div class="wa-stack wa-gap-s">
							<h3 class="feature-card-title" waHeading="m" waText>SPI Extensibility</h3>
							<p waBody="m" waColorText="quiet" waText>Extend the framework by implementing an interface and registering via ServiceLoader or module-info.java. Page configurators, event hooks, and render ordering are all pluggable.</p>
							<div class="feature-card-note" waCaption="s" waColorText="quiet" waFontWeight="semibold" waText>ServiceLoader + module-info</div>
						</div>
					</wa-card>
					<wa-card appearance="outlined" class="feature-card">
						<div class="wa-stack wa-gap-s">
							<h3 class="feature-card-title" waHeading="m" waText>Maven Build Integration</h3>
							<p waBody="m" waColorText="quiet" waText>The Maven plugin discovers components, generates TypeScript, installs npm, and runs the Angular CLI &mdash; all inside mvn install. Hosted mode has no frontend build step at all.</p>
							<div class="feature-card-note" waCaption="s" waColorText="quiet" waFontWeight="semibold" waText>mvn install &mdash; done</div>
						</div>
					</wa-card>
					<wa-card appearance="outlined" class="feature-card">
						<div class="wa-stack wa-gap-s">
							<h3 class="feature-card-title" waHeading="m" waText>IDE-Friendly</h3>
							<p waBody="m" waColorText="quiet" waText>Concrete Java classes with full autocomplete, refactoring, find-usages, and go-to-definition. Works out of the box in IntelliJ, Eclipse, or VS Code.</p>
							<div class="feature-card-note" waCaption="s" waColorText="quiet" waFontWeight="semibold" waText>Zero IDE plugins</div>
						</div>
					</wa-card>
				</div>
				<div class="wa-cluster wa-gap-s">
					<wa-button [routerLink]="['/getting-started']" variant="brand">Getting Started Guide</wa-button>
				</div>
			</div>
		</div>
		<div class="wa-stack wa-gap-m content-section" id="production-ready">
			<wa-divider class="section-divider"></wa-divider>
			<div class="wa-stack wa-gap-s">
				<div class="hero-eyebrow" waCaption="s" waFontWeight="semibold" waText>PRODUCTION READY</div>
				<h2 waHeading="l" waText>Ship with Confidence</h2>
				<p waBody="m" waColorText="quiet" waText>Modular, reactive, and open source &mdash; ready for production workloads.</p>
			</div>
			<div class="wa-stack wa-gap-m">
				<div class="wa-grid wa-gap-m" style="--min-column-size:14rem;">
					<wa-card appearance="outlined" class="feature-card">
						<div class="wa-stack wa-gap-s">
							<h3 class="feature-card-title" waHeading="m" waText>JPMS Modular</h3>
							<p waBody="m" waColorText="quiet" waText>Explicit module-info.java with declared exports, requires, and provides. JLink-ready for minimal container images.</p>
							<div class="feature-card-note" waCaption="s" waColorText="quiet" waFontWeight="semibold" waText>module-info.java</div>
						</div>
					</wa-card>
					<wa-card appearance="outlined" class="feature-card">
						<div class="wa-stack wa-gap-s">
							<h3 class="feature-card-title" waHeading="m" waText>Reactive Non-Blocking</h3>
							<p waBody="m" waColorText="quiet" waText>Hosted mode runs on the Vert.x 5 event loop &mdash; no thread-per-request overhead. Angular SPAs are static files served from any host or CDN.</p>
							<div class="feature-card-note" waCaption="s" waColorText="quiet" waFontWeight="semibold" waText>Event-loop server</div>
						</div>
					</wa-card>
					<wa-card appearance="outlined" class="feature-card">
						<div class="wa-stack wa-gap-s">
							<h3 class="feature-card-title" waHeading="m" waText>Apache 2.0 Open Source</h3>
							<p waBody="m" waColorText="quiet" waText>Permissive licensing. Fork it, extend it, ship it. Active development with snapshot builds on Maven Central.</p>
							<div class="feature-card-note" waCaption="s" waColorText="quiet" waFontWeight="semibold" waText>Apache-2.0 license</div>
						</div>
					</wa-card>
				</div>
				<div class="wa-cluster wa-gap-s">
					<wa-button href="https://github.com/GedMarc/JWebMP" target="_blank" variant="brand">View on GitHub</wa-button>
				</div>
			</div>
		</div>
		<div class="wa-stack wa-gap-m content-section cta-section">
			<wa-divider class="section-divider"></wa-divider>
			<div class="wa-stack wa-gap-s">
				<h2 waHeading="l" waText>Ready to Build?</h2>
				<p waBody="m" waColorText="quiet" waText>From zero to production Angular app &mdash; entirely in Java.</p>
			</div>
			<div class="wa-stack wa-gap-m">
				<p waBody="l" waText>Stop context-switching between Java and TypeScript. Build your entire web application in the language you already know.</p>
				<div class="wa-cluster wa-gap-s hero-ctas">
					<wa-button [routerLink]="['/getting-started']" variant="brand">Get Started</wa-button>
					<wa-button [routerLink]="['/about']" appearance="outlined" variant="neutral">About JWebMP</wa-button>
					<wa-button appearance="outlined" href="https://github.com/GedMarc/JWebMP" target="_blank" variant="neutral">View on GitHub</wa-button>
				</div>
			</div>
		</div>
	</div>
</div>